import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type JSX,
  type MouseEvent,
} from 'react';
import backButtonDark from '@/assets/svg/static/back-button-dark.svg';
import Heart from '@/assets/svg/static/heart.svg';
import shop from '@/assets/svg/static/shop.svg';
import Tryon from '@/assets/svg/static/tryon.svg';
import { Carousel, DoubleColumnProductList } from '@/components/features';
import { SizePills } from '@/components/ui';
import {
  MAKRKETPLACE_IMAGE_MAP,
  type MarketplaceKey,
} from '@/constants/marketplaceImage';
import { EVENTS, PROPERTIES, trackEvent } from '@/services/posthog';
import {
  fetchProductDataById,
  fetchSimilarProductsWithBox,
} from '@/services/slaydAPI';
import useDownloadAppSheetStore from '@/stores/DownloadAppSheetStore';
import useSlaylistSelectorStore from '@/stores/SlaylistSelectorStore';
import type { Product } from '@/types/Product.types';
import {
  decodeBase64Url,
  formatIndianPrice,
  toTitleCase,
  updatedImageUrl,
} from '@/utils/formatting';
import { openProductInApp } from '@/utils/openInApp';
import styles from './ProductDetailPage.module.css';

// TO-DO : refactor this page

const ProductDetailPage = (): JSX.Element => {
  const location = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();

  // Use id as component key to force re-render when product changes
  const componentKey = id || Date.now();
  const scrollRef = useRef<HTMLDivElement>(null);

  const [productDetails, setProductDetails] = useState<Product>({} as Product);
  const [isProductLoading, setIsProductLoading] = useState<boolean>(true);
  const [similarProducts, setSimilarProducts] = useState<Product[]>([]);
  const [cache, setCache] = useState<Record<string, Product[]>>({});

  // Loading state
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const currentProductIdRef = useRef<string>('');

  // Add ref to track if similar products have been fetched for current product
  const similarProductsFetchedRef = useRef<string>('');

  // Add ref to prevent concurrent API calls
  const isApiCallInProgressRef = useRef<boolean>(false);

  // Add ref to track current product's image URL
  const currentProductImageUrlRef = useRef<string>('');

  const productSlaylistOverrides = useSlaylistSelectorStore(
    (state) => state.productSlaylistOverrides
  );

  // Ref to track overrides without causing re-renders
  const productSlaylistOverridesRef = useRef(productSlaylistOverrides);
  productSlaylistOverridesRef.current = productSlaylistOverrides;

  const { openSheet: openDownloadSheet } = useDownloadAppSheetStore();

  const handleLikeClick = (
    e: React.MouseEvent<HTMLButtonElement | HTMLDivElement>
  ): void => {
    e.preventDefault();
    e.stopPropagation();

    openProductInApp({
      productId: id,
      fallback: () => {
        openDownloadSheet('personalize');
      },
    });
  };

  const handleTryOnClick = (
    e: React.MouseEvent<HTMLButtonElement | HTMLDivElement>
  ): void => {
    e.preventDefault();
    e.stopPropagation();
    openProductInApp({
      productId: id,
      fallback: () => {
        openDownloadSheet('try-on');
      },
    });
  };

  const fetchProductDetails = useCallback(
    async (options?: { isBackgroundUpdate?: boolean }) => {
      if (!id) return;

      try {
        // Only show the main skeleton loader on initial page load
        if (!options?.isBackgroundUpdate) {
          setIsProductLoading(true);
        }

        const productData = await fetchProductDataById(id);
        if (productData) {
          if (productData.isSlaydOriginals) {
            let trackerCurator = '';
            const keyParam = new URLSearchParams(location.search).get('key');
            if (keyParam) {
              try {
                const decodedStr = decodeBase64Url(keyParam);
                trackerCurator = decodedStr.trim().split('|')[0] || '';
              } catch (e) {
                console.error(
                  'Error decoding curator name in PDP redirect:',
                  e
                );
              }
            }

            trackEvent(EVENTS.PDP_V1_REDIRECT_TO_V2, {
              [PROPERTIES.PRODUCT_ID]: id,
              [PROPERTIES.CURATOR_NAME]: trackerCurator,
              [PROPERTIES.CURATION_ID]:
                new URLSearchParams(location.search).get('curation_id') || '',
            });

            navigate(`/product/v2/${id}${location.search}`, { replace: true });
            return;
          }

          // Transform the API response to match Product interface
          const transformedProduct: Product = {
            id: productData.id,
            imageUrl: productData.imageUrl,
            price: productData.price,
            discountPrice: productData.discountPrice,
            brand: productData.brand,
            name: productData.name,
            productLink: productData.productLink,
            marketplace: productData.marketplace,
            wishlists: productData.wishlists,
            availableSizes: productData.availableSizes,
            isAvailable: productData.isAvailable,
            secondaryImages: productData.secondaryImages,
            isSlaydOriginals: false,
          };

          setProductDetails(transformedProduct);
        }
      } catch (error) {
        console.error('Error fetching product details:', error);
      } finally {
        // Ensure the main loader is turned off only if it was turned on
        if (!options?.isBackgroundUpdate) {
          setIsProductLoading(false);
        }
      }
    },
    [id, navigate, location.search]
  );

  useEffect(() => {
    if (id) {
      const locationStateProduct = location.state?.productData;
      // If we navigate to this page and have the product data already in location state
      if (locationStateProduct && locationStateProduct.id.toString() === id) {
        if (locationStateProduct.isSlaydOriginals) {
          navigate(`/product/v2/${id}${location.search}`, {
            replace: true,
            state: location.state,
          });
          return;
        }
        const override =
          productSlaylistOverridesRef.current[locationStateProduct.id];
        if (override !== undefined) {
          locationStateProduct.wishlists = override
            ? [{ product_list__name: override.name }]
            : [];
        }
        // Ensure availableSizes is included in the product details
        setProductDetails({
          ...locationStateProduct,
          availableSizes: locationStateProduct.availableSizes || null,
          isSlaydOriginals: false,
        });
        setIsProductLoading(false);
      } else {
        // Otherwise, fetch from the API
        fetchProductDetails();
      }
    }
    // Reset state when ID changes
    currentProductIdRef.current = id || '';
    currentProductImageUrlRef.current = ''; // Reset the image URL ref
    similarProductsFetchedRef.current = ''; // Reset the fetch tracking ref
    setSimilarProducts([]);
    setPage(1);
    setHasMore(true);
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [id, fetchProductDetails, navigate, location.state, location.search]);

  // Handle slaylist override updates without affecting similar products
  useEffect(() => {
    if (productDetails?.id) {
      const override = productSlaylistOverrides[productDetails.id];
      if (override !== undefined) {
        // Only update the wishlists property, not the entire product details
        setProductDetails((prev) => ({
          ...prev,
          wishlists: override
            ? [{ productList_Id: 0, productList_Name: override.name }]
            : [],
        }));
      }
    }

    // Update the current product image URL ref when product details change
    if (productDetails?.imageUrl) {
      currentProductImageUrlRef.current = productDetails.imageUrl;
    }
  }, [productSlaylistOverrides, productDetails?.id, productDetails?.imageUrl]);

  const fetchData = useCallback(
    async ({
      imageUrl,
      pageNumber,
      useCache = true,
    }: {
      imageUrl: string;
      pageNumber: number;
      useCache?: boolean;
    }) => {
      // Prevent concurrent API calls
      if (isApiCallInProgressRef.current) {
        return;
      }

      // Use current product ID from ref to avoid closure issues
      const currentId = currentProductIdRef.current;

      // Make cache key more specific by including product ID
      const cacheKey = `${currentId}_${imageUrl}_page_${pageNumber}`;

      if (useCache && cache[cacheKey]) {
        // Don't set loading state or make API call if data is cached
        return;
      }

      try {
        isApiCallInProgressRef.current = true;
        setIsLoading(true);

        const newProducts = await fetchSimilarProductsWithBox({
          imageUrl: updatedImageUrl(imageUrl),
          pageNum: pageNumber,
        });

        if (newProducts.length === 0) {
          setHasMore(false);
        }

        // Only update products if this is for the current product
        if (currentId === currentProductIdRef.current) {
          setSimilarProducts((prev) => [...prev, ...newProducts]);
        }

        // Add to cache if allowed
        if (useCache) {
          setCache((prev) => ({
            ...prev,
            [cacheKey]: newProducts, // Store just the new products, not accumulated
          }));
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
        isApiCallInProgressRef.current = false;
      }
    },
    [cache] // Remove id from dependencies since we're using the ref
  );

  // Fetch similar products when product details are loaded - FIXED VERSION
  useEffect(() => {
    if (!productDetails?.imageUrl || !id || isProductLoading) return;

    // Use current product ID and image URL from refs to ensure consistency
    const currentId = currentProductIdRef.current;
    const currentImageUrl = currentProductImageUrlRef.current;

    // Only proceed if we have the current image URL and it matches the product details
    if (!currentImageUrl || currentImageUrl !== productDetails.imageUrl) {
      return;
    }

    // Create a unique key for this product's similar products fetch
    const fetchKey = `${currentId}_${currentImageUrl}`;

    // If we've already fetched similar products for this specific product, don't fetch again
    if (similarProductsFetchedRef.current === fetchKey) {
      return;
    }

    // Check if we have cached data for this specific product
    const cacheKeyPrefix = `${currentId}_${currentImageUrl}_page_`;
    const cachedPages = [];
    let i = 1;

    while (true) {
      const key = `${cacheKeyPrefix}${i}`;
      if (!cache[key]) break;
      cachedPages.push(...cache[key]);
      i++;
    }

    if (cachedPages.length) {
      setPage(i - 1); // Set to the last cached page number
      setSimilarProducts(cachedPages);
      setHasMore(true);
      similarProductsFetchedRef.current = fetchKey;
    } else {
      // Fresh fetch for this product
      setSimilarProducts([]);
      setPage(1);
      setHasMore(true);

      // Mark that we're about to fetch for this product
      similarProductsFetchedRef.current = fetchKey;

      fetchData({
        imageUrl: currentImageUrl,
        pageNumber: 1,
        useCache: true,
      });
    }
  }, [productDetails.imageUrl, id, isProductLoading, fetchData, cache]);

  // Handle pagination - FIXED VERSION
  useEffect(() => {
    if (!productDetails?.imageUrl || !id || isProductLoading) return;

    // Use current product ID and image URL from refs to ensure consistency
    const currentId = currentProductIdRef.current;
    const currentImageUrl = currentProductImageUrlRef.current;

    // Only proceed if we have the current image URL and it matches the product details
    if (!currentImageUrl || currentImageUrl !== productDetails.imageUrl) {
      return;
    }

    // Only fetch if this is the current product and page > 1
    // Also check if we've initialized similar products for this product
    const fetchKey = `${currentId}_${currentImageUrl}`;
    if (
      page > 1 &&
      page < 6 &&
      similarProductsFetchedRef.current === fetchKey
    ) {
      // Check if this page is already cached
      const cacheKey = `${currentId}_${currentImageUrl}_page_${page}`;
      if (cache[cacheKey]) {
        return;
      }

      fetchData({
        imageUrl: currentImageUrl,
        pageNumber: page,
        useCache: true,
      });
    }
  }, [page, fetchData, productDetails.imageUrl, id, isProductLoading, cache]);

  const handleLoadMore = useCallback(() => {
    setPage((prev) => prev + 1);
  }, []);

  const handleShopClick = async (e: MouseEvent): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    openProductInApp({
      productId: id,
      fallback: () => {
        openDownloadSheet('search');
      },
    });
  };

  const filteredSimilarProducts = similarProducts.filter(
    (product) =>
      product.imageUrl !== productDetails.imageUrl &&
      product.name !== productDetails.name
  );

  const cleanProductName = (
    brand?: string,
    productName?: string
  ): string | undefined => {
    if (!brand || !productName) return productName;
    try {
      const regex = new RegExp(brand, 'gi');
      return productName.replace(regex, '').trim();
    } catch (err) {
      console.error(err);
      return productName;
    }
  };

  const toggleFullScreen = (): void => {
    setIsFullScreen(!isFullScreen);

    if (!isFullScreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  };

  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const allImages = [
    productDetails.imageUrl,
    ...(productDetails.secondaryImages || []),
  ];

  const FullScreenImage = (): JSX.Element => {
    return (
      <div className={styles.fullScreenOverlay}>
        <button className={styles.closeButton} onClick={toggleFullScreen}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="#FFFFFF"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <div className={styles.fullScreenImageContainer}>
          <Carousel
            images={allImages.map(updatedImageUrl)}
            altPrefix={productDetails.name}
          />
        </div>
      </div>
    );
  };

  // Show loading state while fetching product details
  if (isProductLoading) {
    return (
      <div className={styles.parentProductDetails}>
        <div className={styles.container}>
          <div className={styles.productImage}>
            <div className={styles.skeletonImage} />
          </div>
          <button className={styles.backButton} onClick={() => navigate(-1)}>
            <img src={backButtonDark} alt="Back" className={styles.backIcon} />
          </button>
          <div className={styles.productInfo}>
            <div className={styles.skeletonText} />
            <div className={styles.skeletonText} />
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className={styles.parentProductDetails}>
      <div className={styles.container} key={componentKey} ref={scrollRef}>
        <div className={styles.productImage} onClick={toggleFullScreen}>
          <Carousel
            images={allImages.map(updatedImageUrl)}
            altPrefix={productDetails.name}
          />

          {!isProductLoading && (
            <>
              <button
                className={styles.likeButton}
                aria-label={'Like'}
                onClick={handleLikeClick}
              >
                <img src={Heart} alt="Heart" />
              </button>

              <button
                className={styles.tryButton}
                aria-label="Try On"
                onClick={handleTryOnClick}
              >
                <img className={styles.TryOnIcon} src={Tryon} alt="Tryon" />
                <span className={styles.tryText}>Try on</span>
              </button>
            </>
          )}
        </div>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          <img src={backButtonDark} alt="Back" className={styles.backIcon} />
        </button>
        <div className={styles.productInfo}>
          {' '}
          <div className={styles.details}>
            <div>
              <div className={styles.productNameContainer}>
                <h2 className={styles.productName}>
                  <span className={styles.brandName}>
                    {toTitleCase(productDetails.brand)}
                  </span>
                  <br />
                  <span>
                    {toTitleCase(
                      cleanProductName(
                        productDetails.brand ?? '',
                        productDetails.name ?? ''
                      ) ?? ''
                    )}
                  </span>
                </h2>
              </div>
              <div className={styles.priceContainer}>
                <span className={styles.price}>
                  ₹{formatIndianPrice(String(productDetails.discountPrice))}
                </span>
                {productDetails.price &&
                  productDetails.price &&
                  productDetails.price !== productDetails.discountPrice && (
                    <p className={styles.mrp}>
                      <span className={styles.strikethrough}>
                        ₹{formatIndianPrice(String(productDetails.price))}
                      </span>
                    </p>
                  )}
              </div>
            </div>
            {productDetails.isAvailable ? (
              <a
                href={productDetails.productLink}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.linkDecoration}
              >
                <button className={styles.shopButton} onClick={handleShopClick}>
                  {MAKRKETPLACE_IMAGE_MAP[
                    productDetails.marketplace as MarketplaceKey
                  ] ||
                  MAKRKETPLACE_IMAGE_MAP[
                    productDetails.brand as MarketplaceKey
                  ] ? (
                    <img
                      src={
                        MAKRKETPLACE_IMAGE_MAP[
                          productDetails.marketplace as MarketplaceKey
                        ] ||
                        MAKRKETPLACE_IMAGE_MAP[
                          productDetails.brand as MarketplaceKey
                        ]
                      }
                      alt={
                        productDetails.marketplace ??
                        productDetails.brand ??
                        'shop'
                      }
                      className={styles.productButtonImgBefore}
                    />
                  ) : (
                    <img
                      src={shop}
                      alt={
                        productDetails.marketplace ??
                        productDetails.brand ??
                        'shop'
                      }
                      className={`${styles.productButtonImgBefore} ${styles.productButtonImgCustom}`}
                    />
                  )}
                  Shop
                </button>
              </a>
            ) : (
              <div className={styles.outOfStockButton}>Out of Stock</div>
            )}
          </div>
        </div>

        <div className={styles.dividerLine} />

        {productDetails.availableSizes && (
          <>
            <SizePills
              availableSizes={productDetails.availableSizes}
              className={styles.sizeContainer}
              onClick={handleShopClick}
            />
            <div className={styles.dividerLine} />
          </>
        )}

        <div className={styles.exploreText}>Explore More</div>
        <DoubleColumnProductList
          products={filteredSimilarProducts}
          className={styles.productList}
          isLoading={isLoading}
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
        />

        {isFullScreen && <FullScreenImage />}
      </div>
    </div>
  );
};

export default ProductDetailPage;
